import { Popover, Radio, Table, Tag } from "antd";
import { useTranslation } from "react-i18next";
import { Integration, SortingOrder, IntegrationSortField } from "../../../api";
import { useIntegrationStateContext } from "../context/IntegrationStateContext";
import { TruncatedLongText } from "../../../util/TruncateLongText";
import { useState } from "react";
import { DownSquareOutlined } from "@ant-design/icons";

interface IntegrationPanelTableProps {
  integrationsData: Integration[];
  integrationsLoading: boolean;
}

export function IntegrationPanelTable({
  integrationsData,
  integrationsLoading,
}: IntegrationPanelTableProps) {
  const { t } = useTranslation();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const {
    integrationSortValues,
    activeCell,
    setActiveCell,
    setIntegrationSortValues,
  } = useIntegrationStateContext();

  const CustomTag = ({
    text,
    color,
  }: {
    text: string | null;
    color: string;
  }) => {
    return (
      <Tag color={color}>
        <div style={{ width: 40, height: 20, textAlign: "center" }}>{text}</div>
      </Tag>
    );
  };

  const sortMenu = (
    <div>
      <div>
        <div
          style={{
            fontWeight: "bold",
            paddingLeft: 8,
            paddingTop: 8,
          }}
        >
          {/* //TODO:i1n8 */}
          Sort by
        </div>
        <Radio.Group
          value={integrationSortValues.sort}
          onChange={(e) =>
            setIntegrationSortValues((prev) => ({
              ...prev,
              sort: e.target.value,
            }))
          }
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            margin: 8,
            padding: 8,
          }}
          options={[
            {
              value: IntegrationSortField.DateCreated,
              label: t("created-date"),
            },
            { value: IntegrationSortField.Name, label: t("name") },
          ]}
        />
      </div>
      <div>
        <div
          style={{
            fontWeight: "bold",
            paddingLeft: 8,
          }}
        >
          {/* //TODO:i1n8 */}
          Order by
        </div>
        <Radio.Group
          value={integrationSortValues.order}
          onChange={(e) =>
            setIntegrationSortValues((prev) => ({
              ...prev,
              order: e.target.value,
            }))
          }
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            margin: 8,
            padding: 8,
          }}
          //TODO:i1n8
          options={[
            { value: SortingOrder.Ascending, label: "Ascending" },
            { value: SortingOrder.Descending, label: "Descending" },
          ]}
        />
      </div>
    </div>
  );

  const columns = [
    {
      title: (
        <span
          style={{
            fontWeight: "bold",
            display: "flex",
            justifyContent: "space-between",
            gap: 8,
          }}
        >
          {t("translations:integrations")}
          <Popover
            content={sortMenu}
            trigger="click"
            open={popoverOpen}
            onOpenChange={(open) => setPopoverOpen(open)}
            placement="bottomRight"
            overlayInnerStyle={{ padding: 0 }}
            style={{ height: 400 }}
          >
            <DownSquareOutlined className="icon" />
          </Popover>
        </span>
      ),
      render: (i: Integration) => (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <TruncatedLongText
            text={i.name}
            maxLength={i.protocol === "HTTP" ? "140px" : "250px"}
            id={i.id.toString()}
          />
          <div>
            <CustomTag
              text={i.protocol}
              color={i.protocol === "HTTP" ? "green" : "blue"}
            />
            {i.protocol === "HTTP" && (
              <CustomTag
                text={i.method}
                color={i.method === "PUT" ? "red" : "purple"}
              />
            )}
          </div>
        </div>
      ),
      key: "name",
      onCell: (_record: Integration, rowIndex?: number) => {
        return {
          onClick: () => {
            if (rowIndex !== undefined) {
              setActiveCell({
                row: rowIndex,
                col: "id",
                data: _record,
              });
            }
          },
          className:
            _record.id && activeCell?.data.id === _record.id
              ? "selected-row-solid highlighted-row-solid"
              : "highlighted-row-solid",
        };
      },
    },
  ];

  return (
    <Table
      sticky
      bordered={false}
      className="no-bottom-border"
      size="middle"
      dataSource={integrationsData}
      pagination={false}
      loading={integrationsLoading}
      rowKey="id"
      style={{ minWidth: 300 }}
      data-testid="integration-panel-table"
      columns={columns}
    />
  );
}
